import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/da2jj92amq5jpe1hru7g";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get("company_alt") as string) || "";

    setSending(true);
    setStatus("idle");
    setFormError("");

    // 蜜罐：命中则模拟成功，不真正提交
    if (honeypot.trim() !== "") {
      setSending(false);
      setStatus("success");
      form.reset();
      return;
    }

    formData.delete("company_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") payload.append(key, value);
    });

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (!response.ok || !parsed || parsed.code !== "OK") {
        setSending(false);
        setStatus("error");
        setFormError(serverMsg && serverMsg !== "OK" ? String(serverMsg) : "提交失败，请稍后重试。");
        return;
      }
      setSending(false);
      setStatus("success");
      form.reset();
    } catch {
      setSending(false);
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">Get in Touch</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">联系我们</h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
              无论是作品投稿、图集建议，还是商业合作，欢迎随时留言。我们会认真阅读每一封来信，
              并将你的反馈转化为更好的视觉体验。
            </p>
            <div className="mt-7 space-y-4">
              {[
                { icon: "ri-mail-line", title: "邮箱", desc: "hello@liulishenshe.example" },
                { icon: "ri-time-line", title: "回复时效", desc: "工作日 48 小时内回复" },
                { icon: "ri-chat-smile-3-line", title: "投稿通道", desc: "留下作品链接与简介即可" },
              ].map((c) => (
                <div key={c.title} className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500/10 text-xl text-primary-600">
                    <i className={c.icon}></i>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{c.title}</p>
                    <p className="text-sm text-foreground-500">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-lg font-bold text-foreground-950">留下你的留言</h3>
            <p className="mt-1 text-sm text-foreground-500">我们期待听到你的声音。</p>

            <form className="mt-6 space-y-4" data-readdy-form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-700">留言主题</label>
                <select
                  id="subject"
                  name="subject"
                  className="w-full cursor-pointer rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  <option>作品投稿</option>
                  <option>图集建议</option>
                  <option>商业合作</option>
                  <option>其他</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-700">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  maxLength={500}
                  placeholder="请描述你的想法或需求（最多 500 字）"
                  className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                ></textarea>
              </div>

              {/* 蜜罐字段 */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "success" && (
                <p className="rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  提交成功，感谢你的留言，我们会尽快回复！
                </p>
              )}
              {status === "error" && (
                <p className="rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800">{formError}</p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="inline-flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? "提交中..." : "提交留言"}
                <i className="ri-send-plane-line ml-2"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}